import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { I18nProvider, useI18n } from './contexts/I18nContext';
import { UiVisibilityProvider, useUiVisibility } from './contexts/UiVisibilityContext';
import { Sidebar } from './components/navigation/Sidebar';
import { DashboardPage } from './pages/DashboardPage';
import { DevicesPage } from './pages/DevicesPage';
import { ClientsPage } from './pages/ClientsPage';
import { UsersPage } from './pages/UsersPage';
import { DeviceTypesPage } from './pages/DeviceTypesPage';
import { OtaUpdatesPage } from './pages/OtaUpdatesPage';
import { ConfigTemplatesPage } from './pages/ConfigTemplatesPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { SettingsPage } from './pages/SettingsPage';
import { WhoAmIPage } from './pages/WhoAmIPage';
import { ApiStatusIndicator } from './components/common/ApiStatusIndicator';
import { HeaderLanguageSelector } from './components/common/HeaderLanguageSelector';
import { DebugMenu } from './components/debug/DebugMenu';
import { applyThemeColors } from './utils/themeUtils';
import config from './config/env';
import './App.css';
import './styles/auth.css';
import './styles/devices.css';
import './styles/clients.css';
import './styles/modal.css';
import './styles/api-status.css';
import './styles/pages.css';
import './styles/dashboard.css';
import './styles/audit.css';
import './styles/dark-theme.css';
import './styles/enhanced-dark-theme.css';

// Main Layout component with sidebar
const MainLayout = () => {
  const { isAuthenticated, loading } = useAuth();
  const { t } = useI18n();
  const location = useLocation();

  // Show loading state while auth is being determined
  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="main-content">
          <div className="auth-loading-page">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t('common.loading')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="main-content">
        {isAuthenticated ? (
          <ErrorBoundary name={`route:${location.pathname}`} resetKey={location.pathname}>
            <Routes>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/devices" element={<DevicesPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/device-types" element={<DeviceTypesPage />} />
              <Route path="/ota-updates" element={<OtaUpdatesPage />} />
              <Route path="/config-templates" element={<ConfigTemplatesPage />} />
              <Route path="/audit-log" element={<AuditLogPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              {/* /whoami — local-only diagnostic. Gated on config.dev.debug
                  (only the localhost + test env profiles enable it; the
                  production env profile sets debug=false), so the route
                  never appears in production builds even if someone types
                  the URL. Renders the current user's roles, useClientAccess
                  flags and raw JWT claims; also console.logs the same. */}
              {config.dev.debug && (
                <Route path="/whoami" element={<WhoAmIPage />} />
              )}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </ErrorBoundary>
        ) : (
          <div className="login-required-page">
            <div className="login-required-content">
              <div className="login-required-icon">🔒</div>
              <h2>{t('auth.authenticationRequired')}</h2>
              <p>{t('auth.pleaseSignIn')}</p>
              <div className="login-required-features">
                <h3>{t('app.availableFeatures')}:</h3>
                <ul>
                  <li>📱 {t('app.deviceManagementMonitoring')}</li>
                  <li>👥 {t('app.clientOrganizationOverview')}</li>
                  <li>🔧 {t('app.configurationManagement')}</li>
                  <li>📊 {t('app.realtimeApiStatus')}</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AuthCallback = () => {
  const { loading } = useAuth();
  const { t } = useI18n();

  // Redirect to dashboard once authentication resolves (either outcome
  // lands on /, where AuthProvider surfaces the failure state).
  if (!loading) {
    return <Navigate to="/" replace />;
  }

  // Still processing
  return (
    <div className="auth-callback">
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t('auth.processingAuthentication')}</p>
      </div>
    </div>
  );
};

// Reject logoUrl values whose scheme could embed mixed-content warnings or
// non-image data URIs. Accepts http(s), root-relative paths, and data:image/*.
const sanitiseLogoUrl = (raw: string | undefined): string | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('/')) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^data:image\/(png|jpe?g|gif|svg\+xml|webp);/i.test(trimmed)) return trimmed;
  return null;
};

const AppHeader: React.FC = () => {
  const { showApiStatus, showLanguageSelector, ready } = useUiVisibility();
  const safeLogoUrl = sanitiseLogoUrl(config.ui.logoUrl);
  return (
    <header className="App-header">
      <div className="header-content">
        <div className="header-title">
          {safeLogoUrl && (
            <img
              src={safeLogoUrl}
              alt={`${config.ui.companyName} Logo`}
              className="app-logo"
            />
          )}
          <div className="title-text">
            <h1>{config.ui.title}</h1>
            <p>{config.ui.companyName} Device Management System</p>
          </div>
        </div>
        <div className="header-auth">
          <div className="header-status">
            {/* Defer admin-hideable chrome until the visibility settings have
                resolved so nothing flashes visible on first paint only to
                disappear when the server response arrives. `ready` flips
                true immediately for unauthenticated users (no fetch) and
                after fetchEffectiveOnce resolves for authenticated ones. */}
            {ready && showApiStatus && <ApiStatusIndicator />}
            {/* DebugMenu self-gates: it honours `show_debug_menu` (global/role)
                AND the per-user `debug_menu_usernames` allowlist. Mounting it
                unconditionally (once ready) lets a non-empty allowlist render
                the menu for listed users even when `show_debug_menu === false`. */}
            {ready && <DebugMenu />}
            {ready && showLanguageSelector && <HeaderLanguageSelector />}
          </div>
        </div>
      </div>
    </header>
  );
};

function App() {
  useEffect(() => {
    applyThemeColors();
  }, []);

  return (
    <I18nProvider>
      <ThemeProvider>
        <AuthProvider>
          <UiVisibilityProvider>
            <Router>
              <ErrorBoundary name="app-root">
                <div className="App">
                  <AppHeader />

                  <main className="App-main">
                    <Routes>
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route
                        path="/*"
                        element={<MainLayout />}
                      />
                    </Routes>
                  </main>
                </div>
              </ErrorBoundary>
            </Router>
          </UiVisibilityProvider>
        </AuthProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}

export default App;
